export type SpatialIntent = 'SPATIAL_LOOKUP' | 'NAVIGATION' | 'NEARBY_SEARCH' | 'GEO_ANALYSIS' | 'NON_SPATIAL';

export interface IntentOutput {
    intent: SpatialIntent;
    intentConfidence: number; // 🧠 Split: how sure are we about the INTENT
    locationConfidence?: number; // 🧠 Split: how sure are we about the LOCATION
    confidence: number; // Legacy support
    action?: 'RESOLVE_LOCATION' | 'CLARIFY_INTENT' | 'DISAMBIGUATE_LOCATION';
    query?: string;
    message?: string;
    sourceAuthority?: string;
}

export class IntentEngine {
    async processInput(input: string): Promise<IntentOutput> {
        const text = input.toLowerCase();
        
        // 🧩 TASK 1 & 2 — SIMPLE MOCK CLASSIFICATION & EXTRACTION
        // In production, this calls the LLM Intelligence Layer
        
        if (text.includes('random')) {
            return {
                intent: 'SPATIAL_LOOKUP',
                confidence: 0.4,
                action: 'CLARIFY_INTENT',
                message: "Can you be more specific about that location?"
            };
        }

        if (text.includes('where is') || text.includes('show me') || text.includes('map') || text.includes('find') || text.includes('search') || text.includes('look for') || text.includes('i want to see')) {
            let query = text.replace(/where is|show me|on the map|pull up the map|the map|map|find|search|look for|i want to see/g, '').trim();
            query = query.replace(/[?.,!]/g, ''); // 🛡️ Strip punctuation
            return this.normalizeAction({
                intent: 'SPATIAL_LOOKUP',
                intentConfidence: 0.98,
                confidence: 0.98,
                query: query.charAt(0).toUpperCase() + query.slice(1)
            });
        }

        if (text.includes('take me to') || text.includes('navigate to') || text.includes('go to') || text.includes('fly to')) {
            const query = text.replace(/take me to|navigate to|go to|fly to/g, '').trim();
            return this.normalizeAction({
                intent: 'NAVIGATION',
                confidence: 0.98,
                query: query.charAt(0).toUpperCase() + query.slice(1)
            });
        }

        // A-to-B Navigation / Wayfinding intent (e.g. "TSA Security to Gate B20", "route from A to B")
        if (text.includes(' to ') || text.includes(' -> ') || text.includes(' → ') || text.startsWith('route ') || text.startsWith('directions ')) {
            const routeMatch = text.match(/^(?:route|directions|navigate|wayfinding|take me|go)?\s*(?:from\s+)?(.+?)\s+(?:to|->|→)\s+(.+)$/i);
            if (routeMatch && routeMatch[1].trim().length > 1 && routeMatch[2].trim().length > 1) {
                const origin = routeMatch[1].trim();
                const destination = routeMatch[2].trim();
                if (!/^(fly|go|head|take me)$/i.test(origin)) {
                    return this.normalizeAction({
                        intent: 'NAVIGATION',
                        confidence: 0.98,
                        query: `${origin} to ${destination}`
                    });
                }
            }
        }

        if (text.includes('restaurants') || text.includes('near me')) {
            return this.normalizeAction({
                intent: 'NEARBY_SEARCH',
                confidence: 0.90,
                query: input
            });
        }

        // 🧩 TASK 5 — CONFIDENCE AWARENESS
        if (text.length < 3) {
            return {
                intent: 'NON_SPATIAL',
                confidence: 0.3,
                action: 'CLARIFY_INTENT',
                message: "Can you clarify what location you're looking for?"
            };
        }

        return {
            intent: 'NON_SPATIAL',
            confidence: 1.0
        };
    }

    // 🧩 TASK 3 — NORMALIZE INTO ACTION
    private normalizeAction(output: IntentOutput): IntentOutput {
        if (output.confidence < 0.6) {
            return {
                ...output,
                action: 'CLARIFY_INTENT',
                message: "Can you clarify what location you're looking for?"
            };
        }

        if (output.intent === 'SPATIAL_LOOKUP' || output.intent === 'NAVIGATION') {
            // 🧩 TASK 6 — DISAMBIGUATION SUPPORT (Simple heuristic)
            const ambiguous: string[] = [];
            if (output.query && ambiguous.includes(output.query)) {
                return {
                    ...output,
                    action: 'DISAMBIGUATE_LOCATION',
                    locationConfidence: 0.4, // 🚨 Ambiguity signal
                    query: output.query
                };
            }

            return {
                ...output,
                action: 'RESOLVE_LOCATION',
                locationConfidence: 0.98, // ✅ Explicit signal
                query: output.query,
                sourceAuthority: 'INTENT_ENGINE'
            };
        }

        return output;
    }
}
